import { memo, useRef } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import {
  Avatar,
  DueDateChip,
  IssueKeyBadge,
  LabelChip,
  PointsBadge,
  PriorityIcon,
  TypeIcon,
  cn,
  issueLabel,
  type Issue,
} from '@/features/issue'

/**
 * One card on the board.
 *
 * The whole card is the drag handle, which means a click and a drag start the
 * same way — so opening the issue is gated on the pointer having stayed put.
 * dnd-kit's activation constraint already refuses to start a drag under 4px;
 * this guard is the other half of the same threshold, and without it every drop
 * would also open the panel.
 */
function IssueCardImpl({
  issue,
  onOpen,
  selected,
  dragDisabled,
}: {
  issue: Issue
  onOpen?: (ref: string) => void
  selected?: boolean
  dragDisabled?: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: issue.id,
    disabled: dragDisabled,
    data: { type: 'issue', issue },
  })
  const origin = useRef<{ x: number; y: number } | null>(null)

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn('list-none', isDragging && 'opacity-40')}
    >
      <div
        // dnd-kit's attributes already carry role="button" and tabIndex, so
        // they go on first and are not re-declared below.
        {...attributes}
        aria-label={`${issueLabel(issue)}: ${issue.title}`}
        {...listeners}
        onPointerDown={(event) => {
          origin.current = { x: event.clientX, y: event.clientY }
          listeners?.onPointerDown?.(event)
        }}
        onPointerUp={(event) => {
          const start = origin.current
          origin.current = null
          if (!start || !onOpen) return
          const moved = Math.hypot(event.clientX - start.x, event.clientY - start.y)
          if (moved < 4) onOpen(issue.issue_key ?? issue.id)
        }}
        onKeyDown={(event) => {
          // Enter opens the issue; Space is left to dnd-kit's keyboard sensor,
          // which is the only way to reorder a board without a pointer.
          if (event.key === 'Enter' && onOpen) {
            event.preventDefault()
            onOpen(issue.issue_key ?? issue.id)
            return
          }
          listeners?.onKeyDown?.(event)
        }}
        className={cn(
          'cursor-grab touch-none rounded-card border bg-white p-2.5 shadow-sm transition active:cursor-grabbing',
          'hover:border-ink-300 dark:bg-ink-900',
          selected
            ? 'border-brand-400 ring-1 ring-brand-400 dark:border-brand-600'
            : 'border-ink-200 dark:border-ink-800 dark:hover:border-ink-700',
        )}
      >
        <p className="mb-1.5 line-clamp-3 text-sm text-ink-800 dark:text-ink-100">{issue.title}</p>

        {issue.labels && issue.labels.length > 0 && (
          <div className="mb-1.5 flex flex-wrap gap-1">
            {issue.labels.slice(0, 3).map((label) => (
              <LabelChip key={label.id} label={label} />
            ))}
            {issue.labels.length > 3 && (
              <span className="text-[11px] text-ink-400">+{issue.labels.length - 3}</span>
            )}
          </div>
        )}

        <div className="flex items-center gap-1.5">
          <TypeIcon type={issue.issue_type} />
          <IssueKeyBadge issueKey={issueLabel(issue)} done={issue.status === 'done'} />
          <PriorityIcon priority={issue.priority} />
          <DueDateChip dueDate={issue.due_date} done={issue.status === 'done'} />
          <span className="ml-auto flex items-center gap-1.5">
            <PointsBadge points={issue.story_points} />
            <Avatar user={issue.assignee ?? null} size="xs" />
          </span>
        </div>
      </div>
    </li>
  )
}

/* Memoised: onDragOver fires on every pointer move and rebuilds the board
   array, so without this every card in every column re-rendered each frame —
   which is what made dragging between columns stutter. The parent now passes
   stable callbacks, so a card only re-renders when its own issue changes. */
export const IssueCard = memo(IssueCardImpl)

/** The card that follows the cursor. Deliberately static — no sortable hooks. */
export function IssueCardPreview({ issue }: { issue: Issue }) {
  return (
    <div className="w-72 rotate-1 rounded-card border border-brand-300 bg-white p-2.5 shadow-xl shadow-ink-950/20 dark:border-brand-700 dark:bg-ink-900">
      <p className="mb-1.5 line-clamp-3 text-sm text-ink-800 dark:text-ink-100">{issue.title}</p>
      <div className="flex items-center gap-1.5">
        <TypeIcon type={issue.issue_type} />
        <IssueKeyBadge issueKey={issueLabel(issue)} />
        <PriorityIcon priority={issue.priority} />
        <span className="ml-auto flex items-center gap-1.5">
          <PointsBadge points={issue.story_points} />
          <Avatar user={issue.assignee ?? null} size="xs" />
        </span>
      </div>
    </div>
  )
}
